// ─────────────────────────────────────────────────────────────
// Dashboard
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { Area, AreaChart, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { SetupBanner } from "../../components/layout/SetupBanner";
import { SubscriptionBanners } from "../billing/SubscriptionBanners";
import { Card } from "../../components/ui/Card";
import { Pill } from "../../components/ui/Pill";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Tag } from "../../components/ui/Tag";
import { EconomicCalendarWidget } from "../../components/widgets/EconomicCalendarWidget";
import { MarketSentimentWidget } from "../../components/widgets/MarketSentimentWidget";
import { MarketStatusCard } from "../../components/widgets/MarketStatusCard";
import { NewsWidget } from "../../components/widgets/NewsWidget";
import { TraderSentimentWidget } from "../../components/widgets/TraderSentimentWidget";
import { TodaysPerformanceCard } from "../../components/widgets/TodaysPerformanceCard";
import { PropFirmComplianceWidget, FIRM_COMPLIANCE_PRESETS } from "../../components/widgets/PropFirmComplianceWidget";
import { TodaysPlanCard } from "../../components/widgets/TodaysPlanCard";
import { ROISummaryWidget } from "../../components/widgets/ROISummaryWidget";
import { useCalendar } from "../../hooks/useCalendar";
import { useUserRules } from "../../hooks/useUserRules";
import { buildAnalytics, computeWinRate } from "../../lib/analytics";
import { C } from "../../lib/colors";
import { fmtPct, fmt$, isSessionShifted, getActiveSessionDate } from "../../lib/format";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { TradeShareModal } from "../landing/TradeShareModal";

export function Dashboard({
  trades, 
  setView, 
  showSetup, 
  setShowSetup, 
  displayName = "Trader", 
  accounts = [], 
  currentAccountId, 
  setCurrentAccountId,
  profile,
  openTradeReview,
  updateTrade,
  userId
}) {
  const [showRMultiple, setShowRMultiple] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  // Archived accounts (blown or passed-into-funded) are hidden from the
  // Viewing Account selector — they stay reviewable from Portfolio's
  // archive sections, but shouldn't clutter the daily-use dropdown.
  const activeAccounts = Array.isArray(accounts)
    ? accounts.filter(a => !a.is_blown && !a.is_passed)
    : [];

  // ── Single calendar fetch for the whole dashboard ──────────────────────────
  const {
    todayEvents, eventsByDay, marketStatus, nextHighImpact, calLoading,
    dataTrustworthy, calError,
  } = useCalendar();

  // Fetch payouts so equity reflects actual withdrawals
  const [payouts, setPayouts] = useState([]);
  useEffect(() => {
    if (!userId) return;
    authFetch(`${SUPABASE_URL}/rest/v1/payouts?user_id=eq.${userId}&select=account_id,amount,date`)
      .then(r => r.json())
      .then(data => setPayouts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [userId]);

  // Equity card toggle: "equity" shows account balance, "net" shows net P&L
  const [chartView, setChartView] = useState("equity");
  const [showPayouts, setShowPayouts] = useState(false);
  const [showDrawdownLine, setShowDrawdownLine] = useState(false);
  const [showDailyLossLine, setShowDailyLossLine] = useState(false);
  const [showTargetGoalLine, setShowTargetGoalLine] = useState(false);
  // Time range filter: "1W" | "1M" | "3M" | "YTD" | "All"
  const [timeRange, setTimeRange] = useState("All");

  // Dashboard uses its own local account selection so "All Accounts" (null)
  // is a valid choice here without fighting the global auto-select effect in
  // App.js. Initialized from the global currentAccountId on first render.
  // Changing this does NOT update the global state — Dashboard is a read-only
  // consumer of the global selection, not a writer.
  const [dashboardAccountId, setDashboardAccountId] = useState(
    () => currentAccountId || null
  );

  const selectedAccount = Array.isArray(accounts)
    ? accounts.find(a => a.id === dashboardAccountId)
    : null;

  const accountTrades = dashboardAccountId && Array.isArray(accounts)
    ? trades.filter(t => t.account_id === dashboardAccountId)
    : trades;

  // "Today" = the CME futures session currently open. Sessions roll
  // over at 18:00 local time, so after 6 PM we look ahead to tomorrow's
  // session date. Compare via simple string equality to avoid the
  // UTC-roundtrip trap that bit us when using new Date().toDateString().
  const today = getActiveSessionDate();
  const todayTrades = accountTrades.filter(
    t => String(t.trade_date).slice(0, 10) === today
  );
  // Today's net P&L = gross - fees - commissions (matches what hits the broker balance)
  // Canonical win rate (net P&L based, excludes scratch & open from denominator).
  // Returns null when no decisive trades — render "—" in that case.
  const todayWR = computeWinRate(todayTrades);
  const winRate = todayWR.winRate;  // can be null
  const analytics = buildAnalytics(accountTrades);

  // ─────────────────────────────────────────────────────────────
  // EQUITY: starting balance + net P&L
  // - If a specific account is selected, use its starting balance.
  // - If "All Accounts" is selected, sum all starting balances.
  // ─────────────────────────────────────────────────────────────
  const startingBalance = Array.isArray(accounts)
    ? (dashboardAccountId
        ? parseFloat(accounts.find(a => a.id === dashboardAccountId)?.starting_balance || 0)
        : accounts.reduce((s, a) => s + parseFloat(a.starting_balance || 0), 0))
    : 0;
  // netPnl = gross P&L minus fees and commissions. This is the actual
  // amount that has moved the account balance, and is what the broker's
  // balance reflects. analytics.totalPnl is GROSS — don't use it here.
  const netPnl = analytics?.netPnl ?? 0;
  const totalPayouts = payouts
    .filter(p => !dashboardAccountId || p.account_id === dashboardAccountId)
    .reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalEquity = startingBalance + netPnl - totalPayouts;

  const acctDrawdownLimit  = parseFloat(selectedAccount?.drawdown_limit  || 0);
  const acctDailyLossLimit = parseFloat(selectedAccount?.daily_loss_limit || 0);
  const dailyLossThreshold = chartView === "equity"
    ? startingBalance - acctDailyLossLimit
    : -acctDailyLossLimit;

  // Target Goal — only meaningful for Prop Eval accounts (the amount
  // required to pass). Stored in the same profit_target column used
  // elsewhere (PropFirmComplianceWidget, evalSimulator).
  const isEvalAccount = selectedAccount?.account_type === "prop";
  const acctTargetGoal = isEvalAccount ? parseFloat(selectedAccount?.profit_target || 0) : 0;
  const targetGoalThreshold = chartView === "equity"
    ? startingBalance + acctTargetGoal
    : acctTargetGoal;

  const resolvedDrawdownLimit = (() => {
    if (acctDrawdownLimit > 0) return acctDrawdownLimit;
    if (!selectedAccount) return 0;
    const firm = selectedAccount.prop_firm || "";
    const bal  = String(Math.round(parseFloat(selectedAccount.starting_balance || 0)));
    const preset = FIRM_COMPLIANCE_PRESETS[firm];
    return parseFloat((preset?.[bal] || preset?.default)?.drawdown_limit || 0);
  })();

  const resolvedDrawdownType = selectedAccount?.drawdown_type || "eod_trailing";

  // Trailing-drawdown lock offset above starting balance, by prop firm.
  // Verified against each firm's own drawdown documentation (Aug 2026):
  //  - Apex ("Safety Net"): locks at starting balance + drawdown + $100
  //  - MyFundedFutures: locks at starting balance + $100
  //  - Bulenox (Master Account): locks at starting balance + $100
  //  - TopStep: locks exactly at starting balance (no buffer)
  //  - Take Profit Trader (PRO Account Rules, "Intraday Trailing Drawdown"):
  //      locks exactly at starting balance (no buffer)
  // Firms not listed default to $0 (locks at exactly starting balance).
  const DRAWDOWN_LOCK_OFFSET_BY_FIRM = {
    "Apex": 100,
    "MyFundedFutures": 100,
    "Bulenox": 100,
    "TopStep": 0,
    "Take Profit Trader": 0,
  };
  const drawdownLockOffset = DRAWDOWN_LOCK_OFFSET_BY_FIRM[selectedAccount?.prop_firm] ?? 0;

  const sortedPayoutTotals = (() => {
    if (!showPayouts) return [];
    const accountPayouts = payouts
      .filter(p => !dashboardAccountId || p.account_id === dashboardAccountId)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    let cumulative = 0;
    return accountPayouts.map(p => {
      cumulative += parseFloat(p.amount || 0);
      // Store the full ISO date, not a year-stripped "MM-DD" — ISO date
      // strings sort/compare correctly across year boundaries, unlike
      // MM-DD alone (see analytics.js equityCurve comment for why this
      // matters: a Feb-2026 payout must never be treated as occurring
      // before a Nov-2025 trade just because "02" < "11" as strings).
      const fullDate = String(p.date).slice(0, 10);
      return { fullDate, cumulative };
    });
  })();

  // curveDate must be a full ISO date ("YYYY-MM-DD"). ISO strings compare
  // correctly with simple <=, so no Date parsing (and its year-less-string
  // ambiguity) is needed.
  const payoutDeductionAsOf = (curveDate) => {
    if (sortedPayoutTotals.length === 0) return 0;
    const fullDate = String(curveDate).slice(0, 10);
    let result = 0;
    for (const sp of sortedPayoutTotals) {
      if (sp.fullDate <= fullDate) result = sp.cumulative;
      else break;
    }
    return result;
  };

  const drawdownFloorCurve = (() => {
    if (!showDrawdownLine || resolvedDrawdownLimit <= 0) return [];

    if (resolvedDrawdownType === "intraday_trailing") {
      // Build from analytics.equityCurve — already in correct trade order
      const curve = analytics?.equityCurve || [];
      let peak = startingBalance;
      // The trailing floor stops moving permanently once peak balance first
      // reaches Start + DD + lockOffset, locking at Start + lockOffset from
      // that point on — it does NOT keep trailing upward indefinitely.
      const lockTriggerPeak = startingBalance + resolvedDrawdownLimit + drawdownLockOffset;
      const lockedFloorValue = startingBalance + drawdownLockOffset;
      let locked = false;
      return curve.map(p => {
        // Peak must reflect actual account balance (net of payouts), not raw
        // trading equity — otherwise the floor chases a peak that's too high
        // once withdrawals are taken.
        const bal = startingBalance + p.v - payoutDeductionAsOf(p.fullDate);
        if (bal > peak) peak = bal;
        if (!locked && peak >= lockTriggerPeak) locked = true;
        const floor = locked ? lockedFloorValue : peak - resolvedDrawdownLimit;
        return { date: p.date, floor: chartView === "equity" ? floor : floor - startingBalance };
      });
    }

    // EOD trailing or static — one floor point per day
    const byDate = {};
    accountTrades.forEach(t => {
      const d = String(t.trade_date || "").slice(0, 10);
      if (!d) return;
      if (!byDate[d]) byDate[d] = 0;
      byDate[d] += (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
    });
    const sortedDays = Object.keys(byDate).sort();
    let runningBalance = startingBalance;
    let peakBalance    = startingBalance;
    // Same permanent-lock rule as intraday_trailing, applied at EOD
    // granularity: once peak (net of payouts) reaches Start + DD + lockOffset,
    // the floor freezes at Start + lockOffset for good.
    const lockTriggerPeak = startingBalance + resolvedDrawdownLimit + drawdownLockOffset;
    const lockedFloorValue = startingBalance + drawdownLockOffset;
    let locked = false;
    return sortedDays.map(date => {
      runningBalance += byDate[date];
      // Net out payouts so the floor tracks real account balance, not gross
      // trading P&L — otherwise the floor overstates the peak whenever a
      // payout has been taken, and can visually converge on or cross the
      // (payout-adjusted) equity line.
      const netBalance = runningBalance - payoutDeductionAsOf(date);
      if (resolvedDrawdownType === "eod_trailing" && netBalance > peakBalance) {
        peakBalance = netBalance;
      }
      if (resolvedDrawdownType === "eod_trailing" && !locked && peakBalance >= lockTriggerPeak) {
        locked = true;
      }
      const floor = resolvedDrawdownType === "static"
        ? startingBalance - resolvedDrawdownLimit
        : (locked ? lockedFloorValue : peakBalance - resolvedDrawdownLimit);
      return { date: date.slice(5), floor: chartView === "equity" ? floor : floor - startingBalance };
    });
  })();

  // ─────────────────────────────────────────────────────────────
  // TIME-RANGE FILTER for the equity chart
  //   Filters the full equity curve down to the selected range.
  //   The "v" field is cumulative net P&L from the very first trade,
  //   so we don't lose history — we just slice the view.
  // ─────────────────────────────────────────────────────────────
  const filteredEquityCurve = (() => {
    const curve = analytics?.equityCurve || [];
    if (timeRange === "All" || curve.length === 0) return curve;

    // We need full dates to filter. Reconstruct from the sorted trades.
    const sortedTrades = [...accountTrades].sort((a, b) =>
      new Date(a.trade_date + " " + (a.entry_time || a.trade_time || "00:00")) -
      new Date(b.trade_date + " " + (b.entry_time || b.trade_time || "00:00"))
    );

    const cutoff = new Date();
    if      (timeRange === "1W")  cutoff.setDate(cutoff.getDate() - 7);
    else if (timeRange === "1M")  cutoff.setMonth(cutoff.getMonth() - 1);
    else if (timeRange === "3M")  cutoff.setMonth(cutoff.getMonth() - 3);
    else if (timeRange === "YTD") cutoff.setMonth(0, 1);  // Jan 1 of current year
    cutoff.setHours(0, 0, 0, 0);

    // Find the index of the first trade on/after cutoff
    let cutoffIdx = sortedTrades.findIndex(t => new Date(t.trade_date) >= cutoff);
    if (cutoffIdx === -1) return [];  // no trades in this range

    return curve.slice(cutoffIdx);
  })();

  // Net P&L within the selected range (start-to-end of the visible window)
  const rangeNetPnl = (() => {
    if (timeRange === "All") return netPnl;
    if (filteredEquityCurve.length === 0) return 0;
    const endV   = filteredEquityCurve[filteredEquityCurve.length - 1].v;
    // The first point in the slice already includes the trade's P&L,
    // so subtract the previous point's value to get the net within range.
    const fullCurve = analytics?.equityCurve || [];
    const sliceStartIdx = fullCurve.length - filteredEquityCurve.length;
    const baseline = sliceStartIdx > 0 ? fullCurve[sliceStartIdx - 1].v : 0;
    return endV - baseline;
  })();

  const now = new Date();
  const timeStr = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) + " ET";

  // ── Trading rules for this account (per-account scoping) ──────────
  // Source of truth: user_rules table. We look for a max_trades_per_day
  // rule on the current account. The KPI's "of N max" subtitle should
  // ONLY appear when an actual rule is configured in Today's Plan — not
  // when there's only a profile-level default — so users don't see a
  // limit reference that has no matching entry in Today's Plan.
  const { rules: accountRules = [] } = useUserRules(userId, dashboardAccountId);
  const maxTradesRule = (accountRules || []).find(
    r => r.enabled && r.rule_type === "max_trades_per_day"
  );
  const dailyLimit = maxTradesRule?.parameters?.max_trades ?? null;

  // ── Max Daily Drawdown ────────────────────────────────────────────
  // Now sourced from the rules system (per-account). Falls back to the
  // legacy profile.max_daily_drawdown for accounts without a drawdown
  // rule configured, so existing users don't see anything break.
  const drawdownRule = (accountRules || []).find(
    r => r.enabled && r.rule_type === "max_daily_drawdown"
  );
  const maxDailyDrawdown = parseFloat(drawdownRule?.parameters?.max_drawdown)
    || parseFloat(profile?.max_daily_drawdown)
    || 400;

  // ── Average trade today (replaces the duplicate Today P&L KPI) ────
  // Net P&L of each trade: gross - fees - commissions (broker-aligned).
  const todayAvgTrade = todayTrades.length > 0
    ? todayTrades.reduce(
        (s, t) => s + (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0),
        0
      ) / todayTrades.length
    : 0;

  // Calculate 7-day Discipline Trend
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const lastWeekTrades = accountTrades.filter(t => new Date(t.created_at) >= sevenDaysAgo);
  const lastWeekDiscipline = lastWeekTrades.length > 0
    ? lastWeekTrades.reduce((s, t) => s + (t.discipline_score || 7), 0) / lastWeekTrades.length
    : 0;
  const todayDiscipline = todayTrades.length > 0
    ? todayTrades.reduce((s, t) => s + (t.discipline_score || 7), 0) / todayTrades.length
    : 0;
  const disciplineTrend = todayDiscipline - lastWeekDiscipline;

  const isAllAccounts = !dashboardAccountId;

  const kpis = [
    {
      label: isAllAccounts ? "Avg Trade (All)" : "Avg Trade Today",
      value: todayTrades.length > 0
        ? fmt$(todayAvgTrade)
        : "—",
      sub: todayTrades.length > 0
        ? (isAllAccounts
            ? `across ${todayTrades.length} trade${todayTrades.length === 1 ? "" : "s"}`
            : `${todayTrades.length} trade${todayTrades.length === 1 ? "" : "s"}`)
        : "no trades yet",
      color: todayTrades.length === 0
        ? C.muted
        : todayAvgTrade >= 0 ? C.green : C.red,
    },
    {
      label: isAllAccounts ? "Total Trades Today" : "Trades Today",
      value: `${todayTrades.length}`,
      sub: isAllAccounts
        ? "across all accounts"
        : (dailyLimit != null
            ? `of ${dailyLimit} max`
            : "no limit set"),
      color: !isAllAccounts && dailyLimit != null && todayTrades.length >= dailyLimit
        ? C.yellow
        : C.blue,
    },
    { label: "Win Rate Today", value: winRate !== null ? fmtPct(winRate) : "—", color: C.purple },
    {
      label: "Discipline",
      value: analytics ?
        `${Math.round(accountTrades.slice(0, 5).reduce((s, t) => s + (t.discipline_score || 7), 0) /
          Math.min(accountTrades.slice(0, 5).length || 1, 5))}/10` : "—",
      sub: disciplineTrend > 0.1 ? "📈 Improving" : disciplineTrend < -0.1 ? "📉 Declining" : "→ Stable",
      color: C.green,
      onClick: () => setView("discipline")
    },
  ];

  return (
    <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20 }}>
      {/* LEFT COLUMN - MAIN CONTENT */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {showSetup && <SetupBanner onDismiss={() => setShowSetup(false)} />}

      {/* Subscription health banners — shows when cancellation is pending
          or a payment has failed. Returns null otherwise. */}
      <SubscriptionBanners profile={profile} />

      {/* ACCOUNT SELECTOR */}
      {activeAccounts.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Viewing Account</label>
          <select 
            value={dashboardAccountId || ""} 
            onChange={e => setDashboardAccountId(e.target.value || null)}
            style={{ width: "100%", padding: 12, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
          >
            <option value="">All Accounts</option>
            {activeAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, background: `linear-gradient(135deg, ${C.text}, ${C.sub})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {displayName} 👋
          </h1>
          <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13, fontFamily: "monospace" }}>Discipline today. Freedom tomorrow.</p>
        </div>
        <div style={{ textAlign: "right", color: C.muted, fontSize: 11, fontFamily: "monospace" }}>
          <div>{now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
          <div style={{ marginTop: 2, color: C.sub }}>{timeStr}</div>
        </div>
      </div>

      {/* Market Status Card — fed from shared calendar state, no internal fetch */}
      <MarketStatusCard
        marketStatus={marketStatus}
        nextHighImpact={nextHighImpact}
        calError={calError}
      />

      {/* KPI Strip */}
      <div className="fos-grid-stats-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {kpis.map(k => (
          <Card key={k.label} glow={k.color} style={{ padding: 16 }} onClick={k.onClick}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color, fontFamily: "monospace" }}>{k.value}</div>
            {k.sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{k.sub}</div>}
          </Card>
        ))}
      </div>
{/* Quick Actions */}
      <div className="fos-grid-actions-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <Card 
          style={{ cursor: "pointer", transition: "all 0.2s" }} 
          onClick={() => setView("entry")}
          onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, background: C.green + "20", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>✍️</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Log Trade</div>
              <div style={{ fontSize: 12, color: C.muted }}>Manual or CSV Import</div>
            </div>
          </div>
        </Card>

        <Card 
          style={{ cursor: "pointer", transition: "all 0.2s" }} 
          onClick={() => setView("review")}
          onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, background: C.purple + "20", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>🧠</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Review Last Trade</div>
              <div style={{ fontSize: 12, color: C.muted }}>AI Analysis & Feedback</div>
            </div>
          </div>
        </Card>

        <Card 
          style={{ cursor: "pointer", transition: "all 0.2s" }} 
          onClick={() => setView("checklist")}
          onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, background: C.blue + "20", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>✅</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Can I Trade?</div>
              <div style={{ fontSize: 12, color: C.muted }}>Pre-Trade Checklist</div>
            </div>
          </div>
        </Card>
      </div>



      {/* Today's Plan — rule status at a glance */}
      <TodaysPlanCard
        userId={userId}
        accountId={dashboardAccountId}
        accountTrades={accountTrades}
        todayEvents={todayEvents}
        calendarTrustworthy={dataTrustworthy}
      />

      {/* Today's Performance with MDD Toggle */}
      <TodaysPerformanceCard trades={accountTrades} maxDailyDrawdown={maxDailyDrawdown} startingBalance={startingBalance} key={maxDailyDrawdown} />

      {selectedAccount && (
        <PropFirmComplianceWidget
          account={selectedAccount}
          trades={accountTrades}
        />
      )}
      
      {/* Equity Curve */}
      <Card>
        {/* Title row with toggle */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <SectionTitle>{chartView === "equity" ? "Account Equity" : "Net P&L"}</SectionTitle>
          <div style={{
            display: "inline-flex",
            background: "#1a1d2e",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: 2,
          }}>
            {[
              { id: "equity", label: "Equity" },
              { id: "net",    label: "Net P&L" },
            ].map(opt => {
              const active = chartView === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setChartView(opt.id)}
                  style={{
                    padding: "5px 12px",
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    background: active ? C.green + "22" : "transparent",
                    color: active ? C.green : C.muted,
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    letterSpacing: "0.02em",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Big number */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4, flexWrap: "wrap" }}>
          {chartView === "equity" ? (
            <>
              <div style={{ fontSize: 32, fontWeight: 800, color: C.text }}>
                ${totalEquity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: netPnl >= 0 ? C.green : C.red }}>
                  {fmt$(netPnl)} <span style={{ color: C.muted, fontWeight: 400 }}>net all-time</span>
                </div>
                {totalPayouts > 0 && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>
                    −${totalPayouts.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} payouts
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, fontWeight: 800, color: rangeNetPnl >= 0 ? C.green : C.red }}>
                {fmt$(rangeNetPnl)}
              </div>
              {timeRange !== "All" && (
                <div style={{ fontSize: 13, color: C.muted }}>
                  in {timeRange}
                </div>
              )}
            </>
          )}
        </div>

        {/* Caption */}
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
          {chartView === "equity" ? (
            <>
              Starting balance: ${startingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {!dashboardAccountId && Array.isArray(accounts) && accounts.length > 1 ? ` (across ${accounts.length} accounts)` : ""}
            </>
          ) : (
            <>{timeRange === "All" ? "Strategy contribution since you started" : `Net change over the last ${timeRange}`}</>
          )}
        </div>

        {/* Time-range filter */}
        <div style={{
          display: "flex",
          gap: 4,
          marginBottom: 10,
          background: "#1a1d2e",
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: 2,
          width: "fit-content",
        }}>
          {["1W", "1M", "3M", "YTD", "All"].map(opt => {
            const active = timeRange === opt;
            return (
              <button
                key={opt}
                onClick={() => setTimeRange(opt)}
                style={{
                  padding: "4px 10px",
                  fontSize: 10,
                  fontWeight: active ? 700 : 500,
                  background: active ? C.blue + "22" : "transparent",
                  color: active ? C.blue : C.muted,
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  letterSpacing: "0.02em",
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {/* Chart overlay toggles */}
        <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
          {[
            { key: "showPayouts",      setter: setShowPayouts,       label: "Show Payouts",     color: "#f59e0b", active: showPayouts },
            { key: "showDrawdownLine", setter: setShowDrawdownLine,  label: "Drawdown Floor",   color: "#ef4444", active: showDrawdownLine, hide: !resolvedDrawdownLimit },
            { key: "showDailyLoss",    setter: setShowDailyLossLine, label: "Daily Loss Limit", color: "#f97316", active: showDailyLossLine, hide: !acctDailyLossLimit },
            { key: "showTargetGoal",   setter: setShowTargetGoalLine, label: "Target Goal",     color: "#22c55e", active: showTargetGoalLine, hide: !isEvalAccount || !acctTargetGoal },
          ].filter(o => !o.hide).map(opt => (
            <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11, color: opt.active ? opt.color : C.muted, userSelect: "none" }}>
              <input type="checkbox" checked={opt.active} onChange={() => opt.setter(v => !v)} style={{ accentColor: opt.color, cursor: "pointer" }} />
              {opt.label}
            </label>
          ))}
        </div>

        <div style={{ height: 180 }}>
          {filteredEquityCurve.length === 0 ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: C.muted,
              fontSize: 13,
            }}>
              No trades in this range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={(() => {
                  if (resolvedDrawdownType === "intraday_trailing" && showDrawdownLine) {
                    const offset = (analytics?.equityCurve?.length || 0) - filteredEquityCurve.length;
                    return filteredEquityCurve.map((p, i) => {
                      const payoutDeduction = payoutDeductionAsOf(p.fullDate);
                      const base = chartView === "equity" ? startingBalance + p.v - payoutDeduction : p.v;
                      return { ...p, v: base, floor: drawdownFloorCurve[offset + i]?.floor };
                    });
                  }
                  const floorMap = {};
                  drawdownFloorCurve.forEach(d => { floorMap[d.date] = d.floor; });
                  return filteredEquityCurve.map(p => {
                    const date = String(p.date).slice(0, 5);
                    const payoutDeduction = payoutDeductionAsOf(p.fullDate);
                    const base = chartView === "equity" ? startingBalance + p.v - payoutDeduction : p.v;
                    return { ...p, v: base, floor: showDrawdownLine ? floorMap[date] : undefined };
                  });
                })()}
                margin={{ top: 5, right: 5, left: 5, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="eqC" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.green} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  stroke={C.muted}
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                />
                <YAxis
                  domain={(showDrawdownLine && resolvedDrawdownLimit > 0) || (showTargetGoalLine && isEvalAccount && acctTargetGoal > 0)
                    ? ([dataMin, dataMax]) => {
                        const floorVals = showDrawdownLine ? drawdownFloorCurve.map(d => d.floor).filter(Boolean) : [];
                        const minFloor = floorVals.length ? Math.min(...floorVals) : dataMin;
                        const maxWithTarget = showTargetGoalLine && isEvalAccount && acctTargetGoal > 0
                          ? Math.max(dataMax, targetGoalThreshold)
                          : dataMax;
                        const padding = (maxWithTarget - Math.min(dataMin, minFloor)) * 0.05;
                        return [Math.min(dataMin, minFloor) - padding, maxWithTarget + padding];
                      }
                    : ['auto', 'auto']
                  }
                  hide={true}
                />
                <Area type="monotone" dataKey="v" name={chartView === "equity" ? "Equity" : "Net P&L"} stroke={C.green} strokeWidth={3} fill="url(#eqC)" />
                {showDrawdownLine && resolvedDrawdownLimit > 0 && (
                  <Line type="monotone" dataKey="floor" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Drawdown Floor" />
                )}
                {showDailyLossLine && acctDailyLossLimit > 0 && (
                  <ReferenceLine y={dailyLossThreshold} stroke="#f97316" strokeDasharray="4 3" strokeWidth={1.5}
                    label={{ value: "Daily Loss Limit", position: "insideBottomRight", fill: "#f97316", fontSize: 10 }} />
                )}
                {showTargetGoalLine && isEvalAccount && acctTargetGoal > 0 && (
                  <ReferenceLine y={targetGoalThreshold} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                    label={{ value: "Target Goal", position: "insideTopRight", fill: "#22c55e", fontSize: 10 }} />
                )}
                <Tooltip
                  labelFormatter={(label) => `Date: ${label}`}
                  formatter={(v, name) => [
                    `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    name,
                  ]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, marginTop: 8 }}>
          <div>178W • 76L • PF 1.44</div>
          <div style={{ color: C.green }}>All Time</div>
        </div>
      </Card>

      {/* ROI Summary */}
      <ROISummaryWidget
        userId={userId}
        accounts={accounts}
        setView={setView}
      />

      {/* Recent Trades */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <SectionTitle action={<button onClick={() => setView("analytics")} style={{ background: "none", border: "none", color: C.blue, fontSize: 12, cursor: "pointer" }}>View Analytics →</button>}>
            Recent Trades ({selectedAccount ? selectedAccount.name : "All Accounts"})
          </SectionTitle>
          <button
            onClick={() => setShowRMultiple && setShowRMultiple(!showRMultiple)}
            style={{
              padding: "4px 8px",
              background: showRMultiple ? C.blue : C.border,
              color: C.text,
              border: "none",
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            {showRMultiple ? "R-Multiple" : "P&L"}
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>{["Date / Time", "Symbol", "Setup", "Dir", "Result", showRMultiple ? "R-Multiple" : "P&L", "Score", ""].map(h => (
                <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: C.muted, fontWeight: 500, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {[...accountTrades].sort((a, b) => {
                if (a.exited_at && b.exited_at) {
                  const diff = new Date(b.exited_at) - new Date(a.exited_at);
                  if (diff !== 0) return diff;
                  return (Number(a.broker_trade_id) || 0) - (Number(b.broker_trade_id) || 0);
                }
                const realDate = (t) => {
                  const s = t.trade_date || t.created_at?.split("T")[0] || "";
                  const h = parseInt((t.exit_time || t.entry_time || "00").slice(0, 2), 10);
                  if (s && !isNaN(h) && h >= 17) {
                    const d = new Date(s + "T12:00:00"); d.setDate(d.getDate() - 1);
                    return d.toISOString().slice(0, 10);
                  }
                  return s;
                };
                const dA = `${realDate(a)} ${a.exit_time || a.entry_time || "00:00"}`;
                const dB = `${realDate(b)} ${b.exit_time || b.entry_time || "00:00"}`;
                if (dB !== dA) return dB.localeCompare(dA);
                return new Date(b.created_at || 0) - new Date(a.created_at || 0);
              }).slice(0, 5).map((t) => (
                <tr 
                  key={t.id} 
                  onClick={() => openTradeReview(t)}
                  style={{ 
                    borderBottom: `1px solid ${C.border}20`,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    backgroundColor: "transparent"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = C.border + "20"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <td style={{ padding: "10px", color: C.sub, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                    {t.trade_date || t.created_at?.split("T")[0] || "—"}
                    {isSessionShifted(t) && (
                      <span
                        title={`Session day. This trade was entered the evening before (after 6 PM ET), but per CME session conventions it belongs to ${t.trade_date}.`}
                        style={{ marginLeft: 4, opacity: 0.7 }}
                      >
                        🌙
                      </span>
                    )}
                    <br />
                    <span style={{ fontSize: 10 }}>{(t.entry_time || t.trade_time || "")?.slice(0, 5) || "—"}</span>
                  </td>
                  <td style={{ padding: "10px", color: C.text, fontFamily: "monospace", fontWeight: 600 }}>{t.symbol}</td>
                  <td style={{ padding: "10px" }}><Tag label={t.setup_type || "—"} color={C.purple} /></td>
                  <td style={{ padding: "10px", color: t.direction === "Long" ? C.green : C.red, fontWeight: 600 }}>{t.direction === "Long" ? "↑" : "↓"} {t.direction}</td>
                  <td style={{ padding: "10px" }}>
                    <span style={{ background: t.result === "Win" ? C.green + "18" : C.red + "18", color: t.result === "Win" ? C.green : C.red, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{t.result}</span>
                  </td>
                  <td style={{ padding: "10px" }}>
                    {showRMultiple ? (
                      (() => {
                        let rMultiple = null;
                        // Calculate risk from stop loss if available
                        if (t.entry_price && t.stop_loss) {
                          const risk = Math.abs(t.entry_price - t.stop_loss);
                          rMultiple = risk > 0 ? t.pnl / risk : null;
                        }
                        // Otherwise use risk field if available
                        if (rMultiple === null && t.risk) {
                          rMultiple = t.pnl / t.risk;
                        }
                        
                        return rMultiple !== null ? (
                          <span style={{ color: rMultiple > 0 ? C.green : C.red, fontWeight: 600, fontFamily: "monospace" }}>
                            {rMultiple > 0 ? "+" : ""}{rMultiple.toFixed(1)}R
                          </span>
                        ) : (
                          <span style={{ color: C.muted, fontFamily: "monospace" }}>—</span>
                        );
                      })()
                    ) : (
                      <Pill value={t.pnl || 0} />
                    )}
                  </td>
                  <td style={{ padding: "10px", color: !t.discipline_score ? C.muted : t.discipline_score >= 8 ? C.green : t.discipline_score >= 6 ? C.yellow : C.red, fontFamily: "monospace" }}>
                    {t.discipline_score ? `${t.discipline_score}/10` : "—"}
                  </td>
                  <td style={{ padding: "10px" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShareTarget(t); }}
                      title="Share trade"
                      style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "2px 6px", borderRadius: 4 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#4caf7d"}
                      onMouseLeave={e => e.currentTarget.style.color = C.muted}
                    >↑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      </div>

      {/* RIGHT COLUMN - MARKET INTELLIGENCE */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <EconomicCalendarWidget
          todayEvents={todayEvents}
          eventsByDay={eventsByDay}
          calLoading={calLoading}
          dataTrustworthy={dataTrustworthy}
          calError={calError}
        />
        <MarketSentimentWidget />
        <TraderSentimentWidget
          accountTrades={accountTrades}
          accountRules={accountRules}
          profile={profile}
          totalEquity={totalEquity}
          startingBalance={startingBalance}
        />
        <NewsWidget />
      </div>
      {shareTarget && (
        <TradeShareModal
          trade={shareTarget}
          allTrades={accountTrades}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}









// ─────────────────────────────────────────────────────────────
// IMPROVED 3-STAGE TRADE ENTRY
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// FULL TRADE ENTRY - COMPLETE 3-STAGE + ALL FIELDS EDITABLE
// ─────────────────────────────────────────────────────────────
